import { AutoField, FieldLabel } from "@measured/puck";
import { Box, SxProps, useTheme } from "@mui/material";

import NativePuckTitleField from "src/components/Puck/config/fields/NativePuckTitleField";
import { SPACING_OPTIONS } from "src/components/Puck/config/layoutTokens";

export type SpacingSidesValue = {
    top: number;
    right: number;
    bottom: number;
    left: number;
};

type SpacingSidesFieldProps = {
    label: string;
    subtitle?: string;
    icon: Parameters<typeof NativePuckTitleField>[0]["icon"];
    centerLabel?: string;
    value?: Partial<SpacingSidesValue>;
    onChange: (value: SpacingSidesValue) => void;
    readOnly?: boolean;
    sx?: SxProps;
};

const coerce = (v: unknown, fallback: number): number => (typeof v === "number" && !Number.isNaN(v) ? v : fallback);

export const SpacingSidesField = (props: SpacingSidesFieldProps) => {
    const { label, subtitle, icon, centerLabel = "Box", value, onChange, readOnly, sx } = props;
    const theme = useTheme();

    const current: SpacingSidesValue = {
        top: coerce(value?.top, 0),
        right: coerce(value?.right, 0),
        bottom: coerce(value?.bottom, 0),
        left: coerce(value?.left, 0),
    };

    const updateSide = (key: keyof SpacingSidesValue, nextValue: number) => {
        onChange({ ...current, [key]: nextValue });
    };

    return (
        <Box sx={{ ...sx }}>
            <NativePuckTitleField label={label} subtitle={subtitle} icon={icon} />

            <Box sx={{ pt: theme.spacing(1.5) }}>
                <FieldLabel label="" el="div">
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box sx={{ width: 104 }}>
                                <Box sx={{ textAlign: "center", mb: theme.spacing(0.5) }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "var(--puck-color-grey-05)",
                                        }}
                                    >
                                        Top
                                    </Box>
                                </Box>
                                <AutoField
                                    field={{ type: "select", options: SPACING_OPTIONS }}
                                    onChange={(v) => updateSide("top", v as number)}
                                    value={current.top}
                                    readOnly={readOnly}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ width: 104 }}>
                                <Box sx={{ textAlign: "center", mb: theme.spacing(0.5) }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "var(--puck-color-grey-05)",
                                        }}
                                    >
                                        Left
                                    </Box>
                                </Box>
                                <AutoField
                                    field={{ type: "select", options: SPACING_OPTIONS }}
                                    onChange={(v) => updateSide("left", v as number)}
                                    value={current.left}
                                    readOnly={readOnly}
                                />
                            </Box>

                            <Box
                                sx={{
                                    width: 44,
                                    height: 88,
                                    border: "1px dashed rgba(0,0,0,0.25)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px",
                                    color: "#999",
                                }}
                            >
                                {centerLabel}
                            </Box>

                            <Box sx={{ width: 104 }}>
                                <Box sx={{ textAlign: "center", mb: theme.spacing(0.5) }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "var(--puck-color-grey-05)",
                                        }}
                                    >
                                        Right
                                    </Box>
                                </Box>
                                <AutoField
                                    field={{ type: "select", options: SPACING_OPTIONS }}
                                    onChange={(v) => updateSide("right", v as number)}
                                    value={current.right}
                                    readOnly={readOnly}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box sx={{ width: 104 }}>
                                <Box sx={{ textAlign: "center", mb: theme.spacing(0.5) }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "var(--puck-color-grey-05)",
                                        }}
                                    >
                                        Bottom
                                    </Box>
                                </Box>
                                <AutoField
                                    field={{ type: "select", options: SPACING_OPTIONS }}
                                    onChange={(v) => updateSide("bottom", v as number)}
                                    value={current.bottom}
                                    readOnly={readOnly}
                                />
                            </Box>
                        </Box>
                    </Box>
                </FieldLabel>
            </Box>
        </Box>
    );
};

export default SpacingSidesField;
